import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, Phone, Mail, CheckCircle, ChevronLeft, ChevronRight, Users, Star, Camera, ExternalLink, Newspaper, Facebook, Send, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { SEOHead, localBusinessSchema } from '../components/SEOHead';
import { slugify } from '../lib/cities-data';
import { swallowWithLog } from '../lib/logger';
import { VIEWPORT_ONCE } from '../lib/motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.03 } } };

function AgencyMap({ lat, lng, radiusKm, name }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (mapInstanceRef.current) return;
    const zoom = radiusKm <= 25 ? 10 : 9;
    const map = L.map(mapRef.current, { center: [lat, lng], zoom, scrollWheelZoom: false });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 18,
    }).addTo(map);

    L.circle([lat, lng], {
      radius: radiusKm * 1000,
      color: '#3a8f8f',
      fillColor: '#3a8f8f',
      fillOpacity: 0.12,
      weight: 3,
      dashArray: '10, 8',
    }).addTo(map).bindPopup(`<strong>Zone d'intervention</strong><br/>Rayon de ${radiusKm} km`);

    const icon = L.divIcon({
      className: '',
      html: '<div style="background:#3a8f8f;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>',
      iconSize: [18, 18], iconAnchor: [9, 9],
    });
    L.marker([lat, lng], { icon }).addTo(map).bindPopup(`<strong>${name}</strong>`);
    mapInstanceRef.current = map;
    return () => { if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } };
  }, [lat, lng, radiusKm, name]);

  return <div ref={mapRef} style={{ width: '100%', height: '400px', borderRadius: '0.9rem' }} />;
}

function FacebookEmbed({ url }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!url || !containerRef.current) return;
    
    // Extract page name from URL for the plugin
    const encodedUrl = encodeURIComponent(url);
    
    // Load Facebook SDK if not loaded
    if (!window.FB) {
      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/fr_FR/sdk.js#xfbml=1&version=v21.0';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        if (window.FB) window.FB.XFBML.parse(containerRef.current);
      };
      document.body.appendChild(script);
    } else {
      window.FB.XFBML.parse(containerRef.current);
    }
  }, [url]);

  if (!url) return null;

  return (
    <div ref={containerRef} className="flex justify-center bg-white">
      <div 
        className="fb-page" 
        data-href={url}
        data-tabs="timeline"
        data-width="340"
        data-height="400"
        data-small-header="true"
        data-adapt-container-width="true"
        data-hide-cover="false"
        data-show-facepile="false"
      >
        <blockquote cite={url} className="fb-xfbml-parse-ignore">
          <a href={url} className="text-sm text-[#1877F2]">Chargement du flux Facebook...</a>
        </blockquote>
      </div>
    </div>
  );
}

function NewsCarousel({ news, facebookUrl }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (news.length <= 1) return;
    const timer = setInterval(() => { setIndex(i => (i === news.length - 1 ? 0 : i + 1)); }, 5000);
    return () => clearInterval(timer);
  }, [news.length]);

  if (!news || news.length === 0) return null;
  const current = news[index];

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main news card */}
        <div className="lg:col-span-2">
          <motion.div key={index} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
            <Card className="overflow-hidden h-full">
              {current.image && (
                <div className="h-[200px] sm:h-[240px] overflow-hidden">
                  <img src={current.image} alt={current.titre} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Newspaper className="h-4 w-4 text-[hsl(var(--primary))]" />
                  <span className="text-xs text-muted-foreground">{current.date ? new Date(current.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</span>
                </div>
                <h3 className="font-['Space_Grotesk'] font-semibold text-lg mb-2">{current.titre}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{current.contenu}</p>
                {current.lien && (
                  <a href={current.lien} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-[hsl(var(--primary))] font-medium mt-3 hover:underline">
                    Lire la suite <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            </Card>
          </motion.div>
          {news.length > 1 && (
            <div className="flex justify-center items-center gap-4 mt-4">
              <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => setIndex(i => (i === 0 ? news.length - 1 : i - 1))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex gap-1.5">
                {news.map((_, i) => (
                  <button key={`item-${i}`} onClick={() => setIndex(i)}
                    className={`h-2 rounded-full transition-all ${i === index ? 'bg-[hsl(var(--primary))] w-5' : 'bg-[hsl(var(--border))] w-2'}`}
                  />
                ))}
              </div>
              <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => setIndex(i => (i === news.length - 1 ? 0 : i + 1))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Facebook sidebar */}
        <div className="space-y-4">
          {facebookUrl && (
            <Card className="overflow-hidden">
              <div className="p-4 flex items-center gap-2 border-b border-border">
                <div className="w-8 h-8 rounded-lg bg-[#1877F2] flex items-center justify-center">
                  <Facebook className="h-4 w-4 text-white" />
                </div>
                <h3 className="font-['Space_Grotesk'] font-semibold text-sm">Notre page Facebook</h3>
              </div>
              <FacebookEmbed url={facebookUrl} />
              <div className="p-3 border-t border-border">
                <a href={facebookUrl} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" className="w-full bg-[#1877F2] hover:bg-[#1877F2]/90 text-white">
                    <Facebook className="h-4 w-4 mr-2" /> Voir la page complète
                  </Button>
                </a>
              </div>
            </Card>
          )}
          {/* Mini list of other news */}
          {news.length > 1 && (
            <Card className="p-4">
              <h4 className="font-semibold text-xs text-muted-foreground mb-3 uppercase tracking-wide">Autres actualités</h4>
              <div className="space-y-3">
                {news.filter((_, i) => i !== index).slice(0, 3).map((n, i) => (
                  <button key={`item-${i}`} onClick={() => setIndex(news.indexOf(n))} className="w-full text-left hover:bg-[hsl(var(--secondary))]/60 p-2 rounded-lg transition-colors">
                    <p className="text-xs text-muted-foreground">{n.date ? new Date(n.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : ''}</p>
                    <p className="text-sm font-medium line-clamp-2">{n.titre}</p>
                  </button>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function TeamCarousel({ members }) {
  const [index, setIndex] = useState(0);
  
  useEffect(() => {
    if (!members || members.length <= 3) return;
    const timer = setInterval(() => {
      setIndex(i => (i === members.length - 1 ? 0 : i + 1));
    }, 3500);
    return () => clearInterval(timer);
  }, [members]);

  if (!members || members.length === 0) return null;
  const prev = () => setIndex(i => (i === 0 ? members.length - 1 : i - 1));
  const next = () => setIndex(i => (i === members.length - 1 ? 0 : i + 1));
  
  const getVisible = () => {
    if (members.length <= 3) return members;
    const arr = [];
    for (let i = 0; i < 3; i++) {
      arr.push(members[(index + i) % members.length]);
    }
    return arr;
  };
  const visible = getVisible();

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {visible.map((m, i) => (
          <motion.div
            key={`${m._id}-${index}-${i}`}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.1 }}
          >
            <Card className="p-6 text-center hover:shadow-md transition-shadow">
              {m.photo ? (
                <div className="w-28 h-28 rounded-full overflow-hidden mx-auto mb-4 border-2 border-[hsl(var(--primary))]/20 shadow-md">
                  <img src={m.photo} alt={m.prenom} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-28 h-28 rounded-full bg-[hsl(var(--brand-mint))] flex items-center justify-center mx-auto mb-4">
                  <Users className="h-12 w-12 text-[hsl(var(--primary))]" />
                </div>
              )}
              <h3 className="font-['Space_Grotesk'] font-semibold text-lg">{m.prenom}</h3>
              <p className="text-sm text-muted-foreground mt-1">{m.fonction}</p>
            </Card>
          </motion.div>
        ))}
      </div>
      {members.length > 3 && (
        <div className="flex justify-center items-center gap-4 mt-6">
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-full" onClick={prev}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex gap-1.5">
            {members.map((_, i) => (
              <button
                key={`item-${i}`}
                onClick={() => setIndex(i)}
                className={`h-2 rounded-full transition-all ${i === index ? 'bg-[hsl(var(--primary))] w-5' : 'bg-[hsl(var(--border))] w-2'}`}
              />
            ))}
          </div>
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-full" onClick={next}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

function BeforeAfterCarousel({ photos }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (photos.length <= 1) return;
    const timer = setInterval(() => { setIndex(i => (i === photos.length - 1 ? 0 : i + 1)); }, 5000);
    return () => clearInterval(timer);
  }, [photos.length]);

  if (!photos || photos.length === 0) return null;
  const current = photos[index];
  const prev = () => setIndex(i => (i === 0 ? photos.length - 1 : i - 1));
  const next = () => setIndex(i => (i === photos.length - 1 ? 0 : i + 1));

  return (
    <div>
      <motion.div key={index} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} className="relative rounded-2xl overflow-hidden shadow-lg bg-card border border-border">
        <div className="grid grid-cols-2 gap-0.5 bg-border">
          <div className="relative">
            <div className="absolute top-3 left-3 z-10">
              <Badge className="bg-foreground/70 text-white hover:bg-foreground/70 text-xs">Avant</Badge>
            </div>
            <img src={current.before_photo} alt="Avant" className="w-full h-[200px] sm:h-[260px] object-cover" />
          </div>
          <div className="relative">
            <div className="absolute top-3 right-3 z-10">
              <Badge className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))] text-xs">Après</Badge>
            </div>
            <img src={current.after_photo} alt="Après" className="w-full h-[200px] sm:h-[260px] object-cover" />
          </div>
        </div>
        {current.caption && (
          <div className="p-3 text-center">
            <p className="text-sm text-muted-foreground">{current.caption}</p>
          </div>
        )}
      </motion.div>
      {photos.length > 1 && (
        <div className="flex justify-center items-center gap-4 mt-4">
          <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={prev}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex gap-1.5">
            {photos.map((_, i) => (
              <button key={`item-${i}`} onClick={() => setIndex(i)}
                className={`h-2 rounded-full transition-all ${i === index ? 'bg-[hsl(var(--primary))] w-5' : 'bg-[hsl(var(--border))] w-2'}`}
              />
            ))}
          </div>
          <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={next}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

function AgencyContactForm({ agency }) {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ nom: '', telephone: '', email: '', sujet: '', message: '', agency: agency.id, consent_rgpd: false, honeypot: '' });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.nom.trim()) e.nom = 'Obligatoire';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email invalide';
    if (!form.message.trim() || form.message.length < 10) e.message = 'Min. 10 caractères';
    if (!form.consent_rgpd) e.consent_rgpd = 'Obligatoire';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      setSent(true);
      toast.success('Message envoyé !');
    } catch { toast.error('Erreur, veuillez réessayer.'); }
    finally { setLoading(false); }
  };

  if (sent) return (
    <Card className="p-8 text-center">
      <CheckCircle className="h-10 w-10 text-[hsl(var(--primary))] mx-auto mb-4" />
      <h3 className="font-['Space_Grotesk'] font-semibold text-lg mb-2">Message envoyé !</h3>
      <p className="text-sm text-muted-foreground">Nous vous répondrons dans les meilleurs délais.</p>
    </Card>
  );

  return (
    <form onSubmit={handleSubmit}>
      <Card className="p-6 space-y-4">
        <input type="text" name="hp" value={form.honeypot} onChange={e => setForm(p => ({...p, honeypot: e.target.value}))} className="hidden" tabIndex={-1} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="c-nom">Nom *</Label>
            <Input id="c-nom" value={form.nom} onChange={e => setForm(p => ({...p, nom: e.target.value}))} placeholder="Votre nom" className="mt-1" />
            {errors.nom && <p className="text-xs text-destructive mt-1">{errors.nom}</p>}
          </div>
          <div>
            <Label htmlFor="c-tel">Téléphone</Label>
            <Input id="c-tel" value={form.telephone} onChange={e => setForm(p => ({...p, telephone: e.target.value}))} placeholder="06 00 00 00 00" className="mt-1" />
          </div>
        </div>
        <div>
          <Label htmlFor="c-email">Email *</Label>
          <Input id="c-email" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="votre@email.com" className="mt-1" />
          {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
        </div>
        <div>
          <Label htmlFor="c-sujet">Sujet</Label>
          <Input id="c-sujet" value={form.sujet} onChange={e => setForm(p => ({...p, sujet: e.target.value}))} placeholder="L'objet de votre message" className="mt-1" />
        </div>
        <div>
          <Label htmlFor="c-msg">Message *</Label>
          <Textarea id="c-msg" value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} placeholder="Votre message..." className="mt-1" rows={4} />
          {errors.message && <p className="text-xs text-destructive mt-1">{errors.message}</p>}
        </div>
        <label className="flex items-start gap-2 cursor-pointer">
          <Checkbox checked={form.consent_rgpd} onCheckedChange={v => setForm(p => ({...p, consent_rgpd: v}))} className="mt-0.5" />
          <span className="text-xs text-muted-foreground">J'accepte que mes données soient utilisées pour traiter ma demande. *</span>
        </label>
        {errors.consent_rgpd && <p className="text-xs text-destructive">{errors.consent_rgpd}</p>}
        <Button type="submit" disabled={loading} className="w-full">
          {loading ? 'Envoi...' : 'Envoyer le message'} <Send className="ml-2 h-4 w-4" />
        </Button>
      </Card>
    </form>
  );
}

export default function AgencyPage({ agency }) {
  const [team, setTeam] = useState([]);
  const [agencyRating, setAgencyRating] = useState({ average: 0, total: 0 });
  const [beforeAfterPhotos, setBeforeAfterPhotos] = useState([]);
  const [agencyNews, setAgencyNews] = useState([]);
  const [facebookUrl, setFacebookUrl] = useState('');
  const [agencyLogo, setAgencyLogo] = useState('');
  const totalCities = agency.zones.reduce((acc, z) => acc + z.cities.length, 0);

  useEffect(() => {
    let cancelled = false;
    const guard = (setter) => (value) => { if (!cancelled) setter(value); };
    axios.get(`${BACKEND_URL}/api/team/${agency.id}`)
      .then(res => guard(setTeam)(res.data.members)).catch(swallowWithLog('équipe agence'));
    axios.get(`${BACKEND_URL}/api/reviews?agency=${agency.id}&limit=1`)
      .then(res => guard(setAgencyRating)(res.data.stats)).catch(swallowWithLog('note agence'));
    axios.get(`${BACKEND_URL}/api/agency-photos/${agency.id}`)
      .then(res => guard(setBeforeAfterPhotos)(res.data.photos)).catch(swallowWithLog('photos agence'));
    axios.get(`${BACKEND_URL}/api/agency-news/${agency.id}`)
      .then(res => guard(setAgencyNews)(res.data.news)).catch(swallowWithLog('actualités agence'));
    axios.get(`${BACKEND_URL}/api/agency-settings/${agency.id}`)
      .then(res => {
        if (cancelled) return;
        setFacebookUrl(res.data.facebook_url || '');
        setAgencyLogo(res.data.logo_url || '');
      }).catch(swallowWithLog('paramètres agence'));
    return () => { cancelled = true; };
  }, [agency.id]);

  const allCities = agency.zones.flatMap(z => z.cities).join(', ');

  return (
    <div>
      <SEOHead
        title={`${agency.name} — services à domicile Artois`}
        description={`${agency.name}, entreprise indépendante de services à la personne. Ménage, jardinage, repassage, petit bricolage à ${agency.city} et environs (${agency.radiusKm}km). Crédit d'impôt 50%. Tél: ${agency.phone}. Communes desservies : ${allCities.substring(0, 150)}...`}
        path="/zone-intervention"
        schema={localBusinessSchema(agency)}
      />
      {/* Hero */}
      <section className="bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)] py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground">Accueil</Link>
            <span>/</span>
            <span className="text-foreground font-medium">{agency.name}</span>
          </div>
          <Badge className="mb-3 bg-[hsl(var(--brand-mint))] text-[hsl(var(--brand-ink))] hover:bg-[hsl(var(--brand-mint))]">{agency.region}</Badge>
          <div className="flex items-center gap-5 mb-1">
            {agencyLogo && (
              <img src={agencyLogo} alt={`Logo ${agency.name}`} className="h-20 sm:h-24 w-auto rounded-xl shadow-md" />
            )}
            <div>
              <h1 className="font-['Space_Grotesk'] text-3xl sm:text-4xl font-semibold tracking-tight">{agency.name}</h1>
              <p className="text-sm text-muted-foreground mt-1">Entreprise indépendante</p>
            </div>
          </div>
          {agencyRating.total > 0 && (
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} className={`h-5 w-5 ${s <= Math.round(agencyRating.average) ? 'fill-amber-400 text-amber-400' : 'fill-none text-gray-300'}`} />
                ))}
              </div>
              <span className="text-lg font-bold text-[hsl(var(--primary))]">{agencyRating.average}/5</span>
              <span className="text-sm text-muted-foreground">({agencyRating.total} avis)</span>
            </div>
          )}
          <p className="text-muted-foreground max-w-2xl text-base sm:text-lg mb-6">
            Services à la personne dans un rayon de {agency.radiusKm} km. Ménage, jardinage, repassage et plus encore avec crédit d'impôt 50%.
          </p>

          {/* Contact info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center flex-shrink-0">
                <Phone className="h-5 w-5 text-[hsl(var(--primary))]" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Téléphone</p>
                <a href={`tel:${agency.phoneRaw}`} className="font-semibold text-sm text-[hsl(var(--primary))]">{agency.phone}</a>
              </div>
            </Card>
            <Card className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center flex-shrink-0">
                <Mail className="h-5 w-5 text-[hsl(var(--primary))]" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <a href={`mailto:${agency.email}`} className="font-semibold text-sm text-[hsl(var(--primary))]">{agency.email}</a>
              </div>
            </Card>
            <Card className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center flex-shrink-0">
                <MapPin className="h-5 w-5 text-[hsl(var(--primary))]" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Adresse</p>
                <p className="font-semibold text-sm">{agency.address}, {agency.city}</p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Team */}
      {team.length > 0 && (
        <section className="py-12 sm:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <motion.div className="text-center mb-8" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Badge className="mb-3 bg-[hsl(var(--brand-mint))] text-[hsl(var(--brand-ink))] hover:bg-[hsl(var(--brand-mint))]">Notre équipe</Badge>
              <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">L'équipe {agency.name}</h2>
              <p className="text-muted-foreground">Des professionnels dédiés à votre service</p>
            </motion.div>
            <TeamCarousel members={team} />
          </div>
        </section>
      )}

      {/* News / Facebook */}
      {(agencyNews.length > 0 || facebookUrl) && (
        <section className="py-12 sm:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <motion.div className="text-center mb-8" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Badge className="mb-3 bg-[hsl(var(--brand-mint))] text-[hsl(var(--brand-ink))] hover:bg-[hsl(var(--brand-mint))]">Actualités</Badge>
              <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Dernières nouvelles</h2>
              <p className="text-sm text-muted-foreground">Suivez l'actualité de l'{agency.name}</p>
            </motion.div>
            <NewsCarousel news={agencyNews} facebookUrl={facebookUrl} />
          </div>
        </section>
      )}

      {/* Before/After Photos */}
      {beforeAfterPhotos.length > 0 && (
        <section className="py-12 sm:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <motion.div className="text-center mb-8" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Badge className="mb-3 bg-[hsl(var(--brand-mint))] text-[hsl(var(--brand-ink))] hover:bg-[hsl(var(--brand-mint))]">Résultats</Badge>
              <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Avant / Après — nos interventions</h2>
              <p className="text-sm text-muted-foreground">Découvrez la qualité du travail de nos Easers</p>
            </motion.div>
            <BeforeAfterCarousel photos={beforeAfterPhotos} />
          </div>
        </section>
      )}

      {/* Map */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-8" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Zone d'intervention</h2>
            <p className="text-muted-foreground">Rayon de {agency.radiusKm} km &bull; Plus de {totalCities} communes couvertes</p>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="overflow-hidden border border-border shadow-sm">
              <AgencyMap lat={agency.lat} lng={agency.lng} radiusKm={agency.radiusKm} name={agency.name} />
            </Card>
            <div className="space-y-3">
              <Card className="p-4 flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-sm">Intervention rapide</h3>
                  <p className="text-xs text-muted-foreground">Proximité garantie pour des interventions réactives.</p>
                </div>
              </Card>
              <Card className="p-4 flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-sm">Easers locaux</h3>
                  <p className="text-xs text-muted-foreground">Nos Easers habitent dans le secteur et s'engagent dans la durée.</p>
                </div>
              </Card>
              <Card className="p-4 flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-sm">Devis gratuit à domicile</h3>
                  <p className="text-xs text-muted-foreground">Nous nous déplaçons gratuitement chez vous.</p>
                </div>
              </Card>
              <Link to="/devis">
                <Button className="w-full mt-2 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white">
                  Demander un devis <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Cities */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-10" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Villes et communes desservies</h2>
          </motion.div>
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger}>
            {agency.zones.map((zone) => (
              <motion.div key={zone.region} variants={fadeUp}>
                <Card className="p-5 h-full">
                  <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-3 text-[hsl(var(--primary))]">{zone.region}</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {zone.cities.map((city) => (
                      <Link key={city} to={`/services-a-domicile/${slugify(city)}`} data-testid="agency-city-link">
                        <Badge variant="secondary" className="text-xs font-normal hover:bg-[hsl(var(--primary))] hover:text-white transition-colors cursor-pointer">{city}</Badge>
                      </Link>
                    ))}
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
          <div className="mt-8 p-5 rounded-xl bg-[hsl(var(--brand-mint))]/30 text-center">
            <p className="text-sm text-muted-foreground mb-3">Votre commune n'apparaît pas ? Contactez-nous pour vérifier.</p>
            <a href={`tel:${agency.phoneRaw}`}>
              <Button variant="outline" size="sm"><Phone className="mr-2 h-4 w-4" />{agency.phone}</Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section className="py-12 sm:py-16" id="contact">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Contact</Badge>
              <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Contactez l'{agency.name}</h2>
              <div className="space-y-4 mb-6">
                <Card className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center flex-shrink-0">
                    <Phone className="h-5 w-5 text-[hsl(var(--primary))]" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Téléphone</p>
                    <a href={`tel:${agency.phoneRaw}`} className="font-semibold text-[hsl(var(--primary))]">{agency.phone}</a>
                  </div>
                </Card>
                <Card className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-[hsl(var(--primary))]" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <a href={`mailto:${agency.email}`} className="font-semibold text-sm text-[hsl(var(--primary))]">{agency.email}</a>
                  </div>
                </Card>
                <Card className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-[hsl(var(--primary))]" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Horaires</p>
                    <p className="font-semibold text-sm">Lundi - Vendredi : 8h - 18h / Samedi : 9h - 12h</p>
                  </div>
                </Card>
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <AgencyContactForm agency={agency} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--primary))] text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Services à la personne &mdash; {agency.name}</h2>
          <p className="text-primary-foreground/80 mb-8">Crédit d'impôt 50% avec avance immédiate. Devis gratuit et sans engagement.</p>
          <Link to="/devis">
            <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white shadow-lg">
              Demander un devis gratuit <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
